/**
 * PhotoUploader component - drag & drop photo upload.
 */

import React, { useCallback, useState } from 'react';
import { Box, Typography, Button, LinearProgress, Alert } from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import ImageCropDialog from './ImageCropDialog';

interface PhotoUploaderProps {
  onUpload: (file: File) => Promise<void>;
  disabled?: boolean;
}

const PhotoUploader: React.FC<PhotoUploaderProps> = ({ onUpload, disabled = false }) => {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [originalFileName, setOriginalFileName] = useState<string>('photo.jpg');

  const validateFile = (file: File): string | null => {
    // Check file type
    if (!file.type.startsWith('image/')) {
      return 'Please upload an image file (JPG, PNG, or GIF)';
    }

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      return 'Image size must be less than 10MB';
    }

    return null;
  };

  const handleFile = useCallback(async (file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);

    // Store the file name for later use
    setOriginalFileName(file.name);

    // Read the file as data URL for the cropper
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result as string);
    };
    reader.onerror = () => {
      setError('Failed to read image file');
    };
    reader.readAsDataURL(file);
  }, []);

  const handleCropComplete = async (croppedBlob: Blob) => {
    setImageSrc(null);
    setUploading(true);

    try {
      // Create a File from the cropped blob
      const croppedFile = new File([croppedBlob], originalFileName, {
        type: 'image/jpeg',
      });
      await onUpload(croppedFile);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleCropCancel = () => {
    setImageSrc(null);
  };

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragging(false);

      if (disabled || uploading) return;

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        handleFile(files[0]);
      }
    },
    [disabled, uploading, handleFile]
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!disabled && !uploading) {
      setDragging(true);
    }
  }, [disabled, uploading]);

  const handleDragLeave = useCallback(() => {
    setDragging(false);
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFile(e.target.files[0]);
    }
  };

  return (
    <Box>
      <Box
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        sx={{
          border: dragging ? '2px dashed' : '2px dashed',
          borderColor: dragging ? 'primary.main' : 'grey.400',
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          bgcolor: dragging ? 'action.hover' : 'background.paper',
          cursor: disabled || uploading ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s',
          opacity: disabled || uploading ? 0.6 : 1,
        }}
      >
        <CloudUpload sx={{ fontSize: 48, color: 'grey.500', mb: 2 }} />

        <Typography variant="h6" gutterBottom>
          {uploading ? 'Uploading...' : 'Drag & drop photo here'}
        </Typography>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          or
        </Typography>

        <Button
          variant="outlined"
          component="label"
          disabled={disabled || uploading}
          sx={{ mt: 1 }}
        >
          Browse Files
          <input
            type="file"
            hidden
            accept="image/*"
            onChange={handleFileSelect}
            disabled={disabled || uploading}
          />
        </Button>

        <Typography variant="caption" display="block" color="text.secondary" mt={2}>
          Supported: JPG, PNG, GIF (max 10MB)
        </Typography>
      </Box>

      {uploading && (
        <Box mt={2}>
          <LinearProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Image Crop Dialog */}
      {imageSrc && (
        <ImageCropDialog
          open={true}
          imageSrc={imageSrc}
          onCancel={handleCropCancel}
          onCropComplete={handleCropComplete}
        />
      )}
    </Box>
  );
};

export default PhotoUploader;
